import { useCallback } from 'react';
import { useConfig } from '@cp/shared/config';
import { coreLocationApi } from '../api';
import { IAddressOption, toAddressOption } from './addressOption';

type SuggestProvider = (q: string) => Promise<IAddressOption[]>;
const yandexGeoSuggestEndpoint = new URL('https://suggest-maps.yandex.ru/v1/suggest');
const yandexGeoSuggest = (apikey: string, q: string) => {
  const url = new URL(yandexGeoSuggestEndpoint);
  url.searchParams.set('apikey', apikey);
  url.searchParams.set('text', q);
  url.searchParams.set('highlight', '0');
  url.searchParams.set('types', 'locality,house');
  url.searchParams.set('print_address', '1');
  return fetch(url).then((res) => res.json());
};
// https://yandex.ru/maps-api/docs/suggest-api/response.html
type YandexSuggest = { title: { text: string }; address?: { formatted_address?: string } };
const yandexToAddress = (y: YandexSuggest): IAddressOption => ({
  value: y.address?.formatted_address || y.title.text,
  label: y.address?.formatted_address || y.title.text,
});
const useYandexAutocomplete = (): SuggestProvider => {
  const config = useConfig<{ YANDEX_GEOSUGGEST_KEY: string }>();
  const key = config.get('YANDEX_GEOSUGGEST_KEY');
  if (!key) {
    console.warn('Disable yandex suggest api because of empty config: YANDEX_GEOSUGGEST_KEY');
  }
  return useCallback(
    async (q) => {
      if (!key) {
        return [];
      }
      return yandexGeoSuggest(key, q).then((res) => {
        if ('results' in res) {
          return res.results.map(yandexToAddress) as IAddressOption[];
        }
        return [] as IAddressOption[];
      });
    },
    [key],
  );
};

export const useLocationAutocomplete = (cityId?: number) => {
  const [findLocation] = coreLocationApi.endpoints.find.useLazyQuery();
  return useCallback(
    async (query: string): Promise<IAddressOption[]> => {
      const result = await findLocation({ query, cityId }, true).unwrap();
      return result.map(toAddressOption);
    },
    [findLocation, cityId],
  );
};

export const useAddressAutocomplete = (): SuggestProvider => {
  const yandex = useYandexAutocomplete();
  const local = useLocationAutocomplete();
  return useCallback(
    async (q: string) => {
      let locations: IAddressOption[] = [];
      try {
        locations = await yandex(q);
        // Do not throw on yandex request error, fallback to local api
      } catch (_) {}
      if (!locations.length) {
        locations = await local(q);
      }
      return locations;
    },
    [yandex, local],
  );
};
